import type { CommunityMember } from "./CommunityMember";

export const affiliatesProgramPayoutProvider = {
  paypal: "paypal",
  wise: "wise",
} as const;
export type AffiliatesProgramPayoutProvider =
  (typeof affiliatesProgramPayoutProvider)[keyof typeof affiliatesProgramPayoutProvider];

export interface AffiliatesProgram {
  id: number;
  payout_type: AffiliatesProgramPayoutProvider;
}

export const affiliateStatuses = {
  inactive: "inactive",
  invited: "invited",
  enrolled: "enrolled",
  disabled: "disabled",
} as const;
export type AffiliateStatus =
  (typeof affiliateStatuses)[keyof typeof affiliateStatuses];

export type AttributableType =
  | "CommunityMemberCharge"
  | "CommunityMemberSubscription";

export interface AffiliatePromotionalLink {
  promotional_url: string;
  promotional_url_description: string;
}

export interface AffiliateReferralLink {
  affiliate_code: string;
  affiliate_id: number;
  campaign_id: number;
  campaign_name: string;
  paywall_id: number;
  paywall_name: string;
  url: string;
}

export interface AffiliatePayout {
  amount: number;
  amount_as_currency: string;
  id: number;
  status?: string;
}
export interface AffiliateAmountEntriesSummary {
  amount: number;
  amount_as_currency: string;
}

export interface PaywallPriceCommissionDetails {
  commission: string;
  description: string;
  pricing_description: string;
}

export interface PaywallCommissionDetails {
  commission: string;
  description: string;
  prices_commission_details: Array<PaywallPriceCommissionDetails>;
  pricing_description: string;
}

export interface Affiliate {
  commissions_details: string;
  community_id: number;
  community_member: CommunityMember;
  community_member_id: number;
  conversions_count: number;
  due_payout?: AffiliateAmountEntriesSummary;
  enrolled_at: string; // ISO8601 datetime string
  id: number;
  invited_at: string; // ISO8601 datetime string
  leads_count: number;
  manual_update_target_status?: AffiliateStatus;
  paid_payouts?: AffiliateAmountEntriesSummary;
  paypal_email: string;
  paywalls_commission_details: { [key: string]: PaywallCommissionDetails };
  pending_commissions?: AffiliateAmountEntriesSummary;
  processing_payouts?: AffiliateAmountEntriesSummary;
  program: AffiliatesProgram;
  promotional_link?: AffiliatePromotionalLink;
  referral_links: Array<AffiliateReferralLink>;
  status: AffiliateStatus;
  visitors_count: number;
}

export interface InviteNewAndExistingMembersParams {
  existing_member_ids?: (number | undefined)[];
  new_member_emails?: (string | undefined)[];
}

export interface InviteNewAndExistingMembersResponse {
  failures: string[];
  invites_sent: number;
}

export interface AffiliatesPayoutsControls {
  can_export_payouts: boolean;
  can_mark_payouts_as_paid: boolean;
  can_start_payouts: boolean;
}

export interface Attributable {
  can_be_attributed_for_affiliation: boolean;
  has_removable_attribution: boolean;
  id: number;
  paywalls_affiliates_attribution_info: {
    id: number;
    community_member: CommunityMember;
  };
  paywalls_affiliates_campaign_id: number;
  processor_id: string;
}
